import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  ExpandedIndex,
  Flex,
  Icon,
  Input,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBriefcase,
  FaCheck,
  FaDesktop,
  FaDoorClosed,
  FaGraduationCap,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { Building, RoomType } from "../../data/occupancy.api";
import BuildingSelector from "./BuildingSelector";
import CalendarDatePicker from "./CalendarDatePicker";

const BookingSearchForm = () => {
  const highlightColour = useColorModeValue("lancsBlue.500", "lancsBlue.300");
  const highlightFg = useColorModeValue("white", "black");
  const [deskType, setDeskType] = useState<RoomType>();
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState<string>("09:00");
  const [step, setStep] = useState<ExpandedIndex>(0);
  const [searchMethod, setSearchMethod] = useState<"time" | "building">();
  const [building, setBuilding] = useState<Building>();

  const searchURL = useMemo(() => {
    if (deskType && date && time) {
      const dateTime = moment(
        `${moment(date).format("YYYY-MM-DD")} ${time}`,
        "YYYY-MM-DD HH:mm"
      );
      return `/book?deskType=${deskType}&date=${dateTime.toISOString()}`;
    } else if (deskType && building) {
      return `/book?deskType=${deskType}&building=${building.key}`;
    }
  }, [deskType, date, time, building]);

  useEffect(() => {
    if (date) {
      if (moment(date).isSame(moment(), "day")) {
        setTime(moment().format("HH:mm"));
      } else {
        setTime("09:00");
      }
      setStep(3);
    }
  }, [date]);

  useEffect(() => {
    if (building) {
      setStep(999);
    }
  }, [building]);

  return (
    <>
      <Accordion index={step} onChange={setStep}>
        <AccordionItem mb={2} border={0}>
          <AccordionButton
            rounded="md"
            bg={deskType ? "green.500" : undefined}
            color={deskType ? "white" : undefined}
            _hover={deskType ? { bg: "green.600" } : undefined}
            _expanded={
              deskType ? undefined : { bg: highlightColour, color: highlightFg }
            }
          >
            {deskType !== undefined ? <Icon as={FaCheck} mr={2} /> : null}
            <Box flex="1" textAlign="left">
              {deskType
                ? RoomType[deskType]
                : "What type of booking are you looking for?"}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Stack>
              <Button
                leftIcon={<FaBriefcase />}
                onClick={() => {
                  setDeskType(RoomType["Meeting Room"]);
                  setStep(1);
                }}
                w="full"
              >
                Meeting Room
              </Button>
              <Button
                leftIcon={<FaGraduationCap />}
                onClick={() => {
                  setDeskType(RoomType["Study Space"]);
                  setStep(1);
                }}
                w="full"
              >
                Study Space
              </Button>
              <Button
                leftIcon={<FaDesktop />}
                onClick={() => {
                  setDeskType(RoomType["Desk"]);
                  setStep(1);
                }}
                w="full"
              >
                Desk
              </Button>
              <Button
                leftIcon={<FaDoorClosed />}
                onClick={() => {
                  setDeskType(RoomType["Office"]);
                  setStep(1);
                }}
                w="full"
              >
                Office
              </Button>
            </Stack>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem mb={2} border={0} isDisabled={step < 1}>
          <AccordionButton
            rounded="md"
            bg={searchMethod ? "green.500" : undefined}
            color={searchMethod ? "white" : undefined}
            _hover={searchMethod ? { bg: "green.600" } : undefined}
            _expanded={{ bg: highlightColour, color: highlightFg }}
          >
            {searchMethod !== undefined ? <Icon as={FaCheck} mr={2} /> : null}
            <Box flex={1} textAlign="left">
              {searchMethod
                ? `Search by ${searchMethod}`
                : "Select a search method"}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            <Flex gap={2}>
              <Button
                mt={2}
                size="lg"
                w="full"
                onClick={() => {
                  setSearchMethod("time");
                  setStep(2);
                }}
              >
                Time
              </Button>
              <Button
                mt={2}
                size="lg"
                w="full"
                onClick={() => {
                  setSearchMethod("building");
                  setStep(2);
                }}
              >
                Building
              </Button>
            </Flex>
          </AccordionPanel>
        </AccordionItem>

        {searchMethod === "building" ? (
          <AccordionItem mb={2} border={0} isDisabled={step < 2}>
            <AccordionButton
              rounded="md"
              bg={building ? "green.500" : undefined}
              color={building ? "white" : undefined}
              _hover={building ? { bg: "green.600" } : undefined}
              _expanded={{ bg: highlightColour, color: highlightFg }}
            >
              {building !== undefined ? <Icon as={FaCheck} mr={2} /> : null}
              <Box flex="1" textAlign="left">
                {building ? building.name : "Choose a building"}
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel>
              <BuildingSelector value={building} onChange={setBuilding} />
            </AccordionPanel>
          </AccordionItem>
        ) : (
          <>
            <AccordionItem mb={2} border={0} isDisabled={step < 2}>
              <AccordionButton
                rounded="md"
                bg={date ? "green.500" : undefined}
                color={date ? "white" : undefined}
                _hover={date ? { bg: "green.600" } : undefined}
                _expanded={{ bg: highlightColour, color: highlightFg }}
              >
                {date !== undefined ? <Icon as={FaCheck} mr={2} /> : null}
                <Box flex="1" textAlign="left">
                  {date
                    ? moment(date).format("dddd Do MMMM YYYY")
                    : "Choose a date"}
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Button onClick={() => setDate(new Date())} w="full">
                  Today
                </Button>
                <CalendarDatePicker onChange={setDate} />
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem border={0} isDisabled={step < 3}>
              <AccordionButton
                rounded="md"
                _expanded={{ bg: highlightColour, color: highlightFg }}
              >
                <Box flex="1" textAlign="left">
                  Choose a time
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </AccordionPanel>
            </AccordionItem>
          </>
        )}
      </Accordion>
      {searchURL ? (
        <Button
          mt={2}
          colorScheme="lancsBlue"
          variant="outline"
          w="full"
          as={Link}
          to={searchURL ?? ""}
          isDisabled={!searchURL}
        >
          Confirm
        </Button>
      ) : null}
    </>
  );
};

export default BookingSearchForm;
